import { useEffect, useMemo, useRef, useState } from "react";
import {
  api, type FleetRow, type ScheduleRow, type PushBatch, type PushRow,
  type GrantRow, type Script, type Named, type Paged, type User,
} from "../lib/api.js";
import { Drawer, Empty, Notice, Tabs, useAsync, Confirm } from "../components/ui.js";
import { dateTime, relativeTime, bytes } from "../lib/format.js";
import type { PageProps } from "./Shell.js";

/**
 * Everything you do to machines in the plural.
 *
 * The four things here share one idea: you should not have to open a session
 * to find out what a machine is, get a file onto it, run something on it every
 * night, or lend someone access to it for an hour.
 *
 * Inventory is the foundation - disk, uptime, MAC addresses - because none of
 * it comes over the RustDesk protocol. It is collected by running a script, so
 * a machine has to be online once for anything here to know much about it.
 */

const EVERY_CHOICES = [
  { minutes: 15, label: "Every 15 minutes" },
  { minutes: 60, label: "Hourly" },
  { minutes: 60 * 4, label: "Every 4 hours" },
  { minutes: 60 * 24, label: "Daily" },
  { minutes: 60 * 24 * 7, label: "Weekly" },
];

const GRANT_MINUTES = [
  { minutes: 30, label: "30 minutes" },
  { minutes: 60, label: "1 hour" },
  { minutes: 60 * 4, label: "4 hours" },
  { minutes: 60 * 8, label: "A working day" },
  { minutes: 60 * 24, label: "24 hours" },
];

/**
 * Everything a grant can carry.
 *
 * The same set a standing rule can express, so temporary access is never the
 * poorer option - someone lent access for an hour should not have to be given
 * a permanent rule because the thing they need is not on this list.
 */
const GRANT_PERMISSIONS = [
  { id: "keyboard", label: "Keyboard and mouse" },
  { id: "clipboard", label: "Clipboard" },
  { id: "file", label: "File transfer" },
  { id: "audio", label: "Audio" },
  { id: "camera", label: "Camera" },
  { id: "terminal", label: "Terminal" },
  { id: "recording", label: "Recording the session" },
  { id: "restart", label: "Restarting the machine" },
  { id: "block_input", label: "Blocking its own keyboard" },
  { id: "privacy_mode", label: "Blanking its screen" },
  { id: "remote_printer", label: "Printing to it" },
  { id: "remote_modify", label: "Changing its settings" },
  { id: "tunnel", label: "Port tunnelling" },
];

function has(me: PageProps["me"], perm: string): boolean {
  return me.permissions.includes("*")
    || me.permissions.includes(perm)
    || me.permissions.includes(perm.split(":")[0] + ":*");
}

export default function Fleet({ me, onToggleRail }: PageProps) {
  const [tab, setTab] = useState<"inventory" | "push" | "schedules" | "grants">("inventory");

  const tabs = [
    { id: "inventory" as const, label: "Inventory" },
    ...(has(me, "device:connect") ? [{ id: "push" as const, label: "Send a file" }] : []),
    ...(has(me, "script:read") ? [{ id: "schedules" as const, label: "Schedules" }] : []),
    ...(has(me, "acl:read") ? [{ id: "grants" as const, label: "Temporary access" }] : []),
  ];

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Fleet</h1>
          <span className="topbar__hint">What your machines are, and what to do to all of them</span>
        </div>
      </header>

      <div className="content">
        <div className="stack">
          <Tabs tabs={tabs} active={tab} onChange={setTab} />
          {tab === "inventory" ? <Inventory me={me} /> : null}
          {tab === "push" ? <Push /> : null}
          {tab === "schedules" ? <Schedules me={me} /> : null}
          {tab === "grants" ? <Grants me={me} /> : null}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------- inventory ----

/**
 * The disk worth worrying about.
 *
 * A machine reports every fixed disk; a list of them in a table column is
 * unreadable and the one that matters is whichever is closest to full.
 */
function tightest(disks?: Array<{ name: string; total_gb: number; free_gb: number }>) {
  const real = (disks ?? []).filter((d) => d && d.total_gb > 0);
  if (!real.length) return null;
  return real.reduce((a, b) => (a.free_gb / a.total_gb <= b.free_gb / b.total_gb ? a : b));
}

function diskBar(disks?: Array<{ name: string; total_gb: number; free_gb: number }>) {
  const disk = tightest(disks);
  if (!disk) return null;
  const { free_gb: free, total_gb: total, name } = disk;
  const usedPct = Math.round(((total - free) / total) * 100);
  const cls = usedPct >= 90 ? "bar__fill bar__fill--deny"
    : usedPct >= 75 ? "bar__fill bar__fill--warn" : "bar__fill";
  return (
    <div className="bar" title={`${name}: ${free.toFixed(0)} GB free of ${total.toFixed(0)} GB`}>
      <div className={cls} style={{ width: `${usedPct}%` }} />
      <span className="bar__label">{name} {free.toFixed(0)} GB free</span>
    </div>
  );
}

function Inventory({ me }: { me: PageProps["me"] }) {
  const list = useAsync(() => api.get<{ rows: FleetRow[] }>("/api/console/fleet"));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [detail, setDetail] = useState<FleetRow | null>(null);
  const [watchUntil, setWatchUntil] = useState(0);
  const canRun = has(me, "script:run");

  useEffect(() => {
    if (!watchUntil) return;
    const t = setInterval(() => {
      if (Date.now() > watchUntil) { setWatchUntil(0); return; }
      list.reload();
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchUntil]);
  const canWake = has(me, "device:write");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = list.data?.rows ?? [];
    if (!needle) return all;
    return all.filter((r) =>
      `${r.name} ${r.id} ${r.os} ${r.group} ${(r.facts.serial as string) ?? ""}`
        .toLowerCase().includes(needle));
  }, [list.data, q]);

  const stale = rows.filter((r) => !r.collected_at).length;
  const lowDisk = rows.filter((r) => {
    const d = tightest(r.facts.disks);
    return d ? d.free_gb / d.total_gb < 0.1 : false;
  }).length;
  const reboots = rows.filter((r) => r.facts.pending_reboot).length;
  const online = rows.filter((r) => r.online).length;

  async function collect(ids?: string[]) {
    setBusy("collect");
    setNote("");
    try {
      const res = await api.post<{ started: boolean; message?: string; count?: number }>(
        "/api/console/fleet/inventory", ids ? { devices: ids } : { target_type: "all" });
      setNote(res.started
        ? `Asking ${res.count} machine${res.count === 1 ? "" : "s"}. This page updates as they answer.`
        : res.message ?? "Nothing to collect.");
      // Machines answer over the next half minute, not at once, so the page
      // keeps looking for a while rather than refreshing once and stopping.
      if (res.started) setWatchUntil(Date.now() + 60_000);
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function wake(row: FleetRow) {
    setBusy(row.id);
    setNote("");
    try {
      const res = await api.post<{ ok: boolean; message: string; via?: string }>(
        `/api/console/devices/${encodeURIComponent(row.id)}/wake`);
      setNote(res.message);
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  return (
    <>
      <div className="row row--between">
        <input
          className="input" style={{ maxWidth: 280 }} placeholder="Search machines"
          value={q} onChange={(e) => setQ(e.target.value)}
        />
        {canRun ? (
          <button className="btn btn--sm" disabled={busy === "collect"} onClick={() => collect()}>
            {busy === "collect" ? "Collecting\u2026" : "Collect inventory"}
          </button>
        ) : null}
      </div>

      {list.error ? <Notice>{list.error}</Notice> : null}
      {note ? <Notice kind="ok">{note}</Notice> : null}

      <div className="cards">
        <div className="card"><span className="card__n">{online}</span><span className="card__l">Online now</span></div>
        <div className="card"><span className="card__n">{lowDisk}</span><span className="card__l">Under 10% disk</span></div>
        <div className="card"><span className="card__n">{reboots}</span><span className="card__l">Waiting to reboot</span></div>
        <div className="card"><span className="card__n">{stale}</span><span className="card__l">Never inventoried</span></div>
      </div>

      {!list.loading && !rows.length ? (
        <Empty
          title="No machines"
          hint="Machines appear here once they have connected and been let in."
        />
      ) : (
        <div className="panel">
          <table className="data">
            <thead>
              <tr>
                <th>Machine</th><th>Operating system</th><th>Disk</th><th>Uptime</th>
                <th>Collected</th><th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button className="linkish" onClick={() => setDetail(r)}>{r.name}</button>
                    <div className="sub">
                      {r.group ? `${r.group} \u00b7 ` : ""}{r.id}
                      {r.facts.pending_reboot ? <span className="tag tag--warn" style={{ marginLeft: 6 }}>Reboot pending</span> : null}
                    </div>
                  </td>
                  <td>
                    {r.os || "\u2014"}
                    <div className="sub">{r.version ? `RustDesk ${r.version}` : ""}</div>
                  </td>
                  <td>{diskBar(r.facts.disks) ?? <span className="sub">&mdash;</span>}</td>
                  <td>
                    {typeof r.facts.uptime_hours === "number"
                      ? r.facts.uptime_hours >= 48
                        ? `${Math.round(r.facts.uptime_hours / 24)} days`
                        : `${Math.round(r.facts.uptime_hours)} hours`
                      : <span className="sub">&mdash;</span>}
                  </td>
                  <td>
                    {r.collected_at ? relativeTime(r.collected_at) : <span className="sub">Never</span>}
                    {r.error ? <div className="sub sub--warn">{r.error}</div> : null}
                  </td>
                  <td className="right">
                    {r.online ? (
                      canRun ? (
                        <button className="btn btn--ghost btn--sm" onClick={() => collect([r.id])}>
                          Collect
                        </button>
                      ) : null
                    ) : canWake ? (
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={busy === r.id}
                        onClick={() => wake(r)}
                      >
                        {busy === r.id ? "Waking\u2026" : "Wake"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail ? <FactSheet row={detail} onClose={() => setDetail(null)} /> : null}
    </>
  );
}

const FACT_LABELS: Array<{ key: string; label: string; fmt?: (v: unknown) => string }> = [
  { key: "manufacturer", label: "Made by" },
  { key: "model", label: "Model" },
  { key: "serial", label: "Serial number" },
  { key: "cpu", label: "Processor" },
  { key: "memory_gb", label: "Memory", fmt: (v) => `${v} GB` },
  { key: "uptime_hours", label: "Uptime", fmt: (v) => `${Math.round(Number(v))} hours` },
  { key: "last_boot", label: "Last started" },
  { key: "logged_in", label: "Signed in" },
  { key: "domain", label: "Domain" },
];

function FactSheet({ row, onClose }: { row: FleetRow; onClose: () => void }) {
  const macs = (row.facts.macs ?? []) as string[];
  const ips = (row.facts.local_ips ?? []) as string[];
  return (
    <Drawer title={row.name} onClose={onClose}>
      {!row.collected_at ? (
        <Notice kind="warn">
          This machine has never been inventoried. Collect it once while the machine is
          awake &mdash; after that it can also be woken from here.
        </Notice>
      ) : null}
      {row.error ? <Notice>{row.error}</Notice> : null}

      <dl className="facts">
        {FACT_LABELS.filter((f) => row.facts[f.key] !== undefined && row.facts[f.key] !== "").map((f) => (
          <div key={f.key}>
            <dt>{f.label}</dt>
            <dd>{f.fmt ? f.fmt(row.facts[f.key]) : String(row.facts[f.key])}</dd>
          </div>
        ))}
        {(row.facts.disks ?? []).length ? (
          <div>
            <dt>Disks</dt>
            <dd>
              {(row.facts.disks ?? []).map((d) => (
                <div key={d.name}>
                  {d.name} &mdash; {Math.round(d.free_gb)} GB free of {Math.round(d.total_gb)} GB
                </div>
              ))}
            </dd>
          </div>
        ) : null}
        {ips.length ? (
          <div><dt>Local addresses</dt><dd>{ips.join(", ")}</dd></div>
        ) : null}
        {macs.length ? (
          <div><dt>MAC addresses</dt><dd className="mono">{macs.join(", ")}</dd></div>
        ) : null}
        {row.collected_at ? (
          <div><dt>Collected</dt><dd>{dateTime(row.collected_at)}</dd></div>
        ) : null}
      </dl>
    </Drawer>
  );
}

// --------------------------------------------------------------- push ----

function Push() {
  const groups = useAsync(() => api.get<Named[]>("/api/console/device-groups"));
  const batches = useAsync(() => api.get<{ rows: PushBatch[] }>("/api/console/fleet/pushes"));
  const [open, setOpen] = useState(false);
  const [watching, setWatching] = useState("");

  // A push runs on the server, so its rows change while this page is open.
  useEffect(() => {
    const t = setInterval(() => batches.reload(), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="row row--between">
        <p className="sub" style={{ margin: 0 }}>
          Sends one file to every machine in a group, through the same file transfer a
          session uses. Each machine is checked against your access rules first.
        </p>
        <button className="btn btn--primary btn--sm" onClick={() => setOpen(true)}>Send a file</button>
      </div>

      {batches.error ? <Notice>{batches.error}</Notice> : null}

      {!batches.loading && !batches.data?.rows.length ? (
        <Empty title="Nothing sent yet" hint="Files you push to a group appear here with their progress." />
      ) : (
        <div className="panel">
          <table className="data">
            <thead>
              <tr><th>File</th><th>Destination</th><th>Sent by</th><th>Progress</th><th>When</th><th style={{ width: 80 }} /></tr>
            </thead>
            <tbody>
              {(batches.data?.rows ?? []).map((b) => (
                <tr key={b.batch_id}>
                  <td>{b.filename}</td>
                  <td className="mono">{b.target_dir}</td>
                  <td>{b.username}</td>
                  <td>
                    {b.done + b.failed} of {b.total}
                    {b.failed ? <span className="tag tag--deny" style={{ marginLeft: 6 }}>{b.failed} failed</span> : null}
                    {b.done === b.total ? <span className="tag tag--live" style={{ marginLeft: 6 }}>Done</span> : null}
                  </td>
                  <td>{relativeTime(b.started_at)}</td>
                  <td className="right">
                    <button className="btn btn--ghost btn--sm" onClick={() => setWatching(b.batch_id)}>
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open ? (
        <PushDrawer
          groups={groups.data ?? []}
          onClose={() => setOpen(false)}
          onSent={(batch) => { setOpen(false); setWatching(batch); batches.reload(); }}
        />
      ) : null}
      {watching ? <PushDetail batch={watching} onClose={() => setWatching("")} /> : null}
    </>
  );
}

function PushDrawer({
  groups, onClose, onSent,
}: { groups: Named[]; onClose: () => void; onSent: (batch: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [dir, setDir] = useState("");
  const [target, setTarget] = useState("all");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  async function send() {
    if (!file) { setErr("Choose a file to send"); return; }
    if (!dir.trim()) { setErr("Say where it should land on the machine"); return; }
    setBusy(true);
    setErr("");
    try {
      const params = new URLSearchParams({ filename: file.name, dir: dir.trim() });
      if (target === "all") params.set("target_type", "all");
      else { params.set("target_type", "device_group"); params.set("target_id", target); }
      const res = await api.upload<{ batch: string; count: number }>(
        `/api/console/fleet/push?${params.toString()}`, file);
      onSent(res.batch);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      title="Send a file"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" disabled={busy} onClick={send}>
            {busy ? "Sending\u2026" : "Send"}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <Notice kind="warn">
        The file is written as the account RustDesk runs under on each machine, using the
        password you saved for it. A machine with no saved password is skipped.
      </Notice>
      {err ? <Notice>{err}</Notice> : null}

      <label className="field">
        <span>File</span>
        <input
          ref={input} className="input" type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file ? <span className="hint">{file.name} &middot; {bytes(file.size)}</span> : null}
      </label>

      <label className="field">
        <span>Folder on the machine</span>
        <input
          className="input" placeholder="C:\\ProgramData\\Company"
          value={dir} onChange={(e) => setDir(e.target.value)}
        />
        <span className="hint">
          Created if it is missing. Use a path that exists on every machine you are sending to.
        </span>
      </label>

      <label className="field">
        <span>Send to</span>
        <select className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="all">Every machine I can reach</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </label>
    </Drawer>
  );
}

function PushDetail({ batch, onClose }: { batch: string; onClose: () => void }) {
  const rows = useAsync(
    () => api.get<{ rows: PushRow[] }>(`/api/console/fleet/pushes?batch=${encodeURIComponent(batch)}`),
    [batch],
  );
  useEffect(() => {
    const t = setInterval(() => rows.reload(), 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch]);

  return (
    <Drawer title="Where the file got to" onClose={onClose}>
      {rows.error ? <Notice>{rows.error}</Notice> : null}
      <table className="data">
        <thead><tr><th>Machine</th><th>Status</th><th>Finished</th></tr></thead>
        <tbody>
          {(rows.data?.rows ?? []).map((r) => (
            <tr key={r.id}>
              <td>{r.device_name}<div className="sub">{r.device_id}</div></td>
              <td>
                {r.status === "sent" ? <span className="tag tag--live">Delivered</span>
                  : r.status === "refused" ? <span className="tag tag--deny">Refused</span>
                  : r.status === "failed" ? <span className="tag tag--deny">Failed</span>
                  : r.status === "sending" ? <span className="tag tag--warn">Sending</span>
                  : <span className="tag">Queued</span>}
                {r.error ? <div className="sub sub--warn">{r.error}</div> : null}
              </td>
              <td>{r.finished_at ? relativeTime(r.finished_at) : <span className="sub">&mdash;</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Drawer>
  );
}

// ---------------------------------------------------------- schedules ----

function Schedules({ me }: { me: PageProps["me"] }) {
  const list = useAsync(() => api.get<{
    rows: ScheduleRow[]; builtins: Array<{ id: string; name: string }>;
  }>("/api/console/schedules"));
  const [editing, setEditing] = useState<ScheduleRow | "new" | null>(null);
  const [deleting, setDeleting] = useState<ScheduleRow | null>(null);
  const [note, setNote] = useState("");
  const canWrite = has(me, "script:write");
  const canRun = has(me, "script:run");

  async function runNow(row: ScheduleRow) {
    setNote("");
    try {
      const res = await api.post<{ batch: string; message?: string }>(
        `/api/console/schedules/${encodeURIComponent(row.id)}/run`);
      setNote(res.message ?? `Started. Watch it under Scripts \u2192 History.`);
      list.reload();
    } catch (e) {
      setNote((e as Error).message);
    }
  }

  return (
    <>
      <div className="row row--between">
        <p className="sub" style={{ margin: 0 }}>
          A script that runs on its own, on every machine in a group, as often as you say.
        </p>
        {canWrite && canRun ? (
          <button className="btn btn--primary btn--sm" onClick={() => setEditing("new")}>New schedule</button>
        ) : null}
      </div>

      {list.error ? <Notice>{list.error}</Notice> : null}
      {note ? <Notice kind="ok">{note}</Notice> : null}

      {!list.loading && !list.data?.rows.length ? (
        <Empty
          title="Nothing runs on its own yet"
          hint="Collect inventory nightly, clear temporary folders weekly, or run any script from your library."
          action={canWrite && canRun
            ? <button className="btn btn--primary" onClick={() => setEditing("new")}>New schedule</button>
            : undefined}
        />
      ) : (
        <div className="panel">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th><th>Runs</th><th>How often</th><th>On</th><th>Last run</th>
                <th style={{ width: 170 }} />
              </tr>
            </thead>
            <tbody>
              {(list.data?.rows ?? []).map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.name}
                    {!r.enabled ? <span className="tag" style={{ marginLeft: 6 }}>Paused</span> : null}
                    <div className="sub">as {r.run_as_name || "its owner"}</div>
                  </td>
                  <td>{r.script_name}</td>
                  <td>{EVERY_CHOICES.find((c) => c.minutes === r.every_minutes)?.label
                    ?? `Every ${r.every_minutes} minutes`}</td>
                  <td>{r.target_name}</td>
                  <td>{r.last_run_at ? relativeTime(r.last_run_at) : <span className="sub">Never</span>}</td>
                  <td className="right">
                    {canRun ? (
                      <button className="btn btn--ghost btn--sm" onClick={() => runNow(r)}>Run now</button>
                    ) : null}
                    {canWrite ? (
                      <>
                        <button className="btn btn--ghost btn--sm" onClick={() => setEditing(r)}>Edit</button>
                        <button className="btn btn--ghost btn--sm" onClick={() => setDeleting(r)}>Delete</button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <ScheduleDrawer
          value={editing === "new" ? null : editing}
          builtins={list.data?.builtins ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); list.reload(); }}
        />
      ) : null}

      {deleting ? (
        <Confirm
          title="Delete this schedule?"
          body={<>&ldquo;{deleting.name}&rdquo; will stop running. Past runs stay in the history.</>}
          confirmLabel="Delete"
          danger
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await api.del(`/api/console/schedules/${encodeURIComponent(deleting.id)}`);
            setDeleting(null);
            list.reload();
          }}
        />
      ) : null}
    </>
  );
}

function ScheduleDrawer({
  value, builtins, onClose, onSaved,
}: {
  value: ScheduleRow | null;
  builtins: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const scripts = useAsync(() => api.get<Paged<Script>>("/api/console/scripts"));
  const groups = useAsync(() => api.get<Named[]>("/api/console/device-groups"));
  const [name, setName] = useState(value?.name ?? "");
  const [what, setWhat] = useState(
    value ? (value.builtin ? `builtin:${value.builtin}` : `script:${value.script_id}`) : "",
  );
  const [every, setEvery] = useState(value?.every_minutes ?? 60 * 24);
  const [target, setTarget] = useState(
    value?.target_type === "device_group" ? value.target_id : "all",
  );
  const [enabled, setEnabled] = useState(value ? !!value.enabled : true);
  const [err, setErr] = useState("");

  async function save() {
    setErr("");
    try {
      const targetPart = target === "all"
        ? { target_type: "all", target_id: "" }
        : { target_type: "device_group", target_id: target };
      if (value) {
        await api.put(`/api/console/schedules/${encodeURIComponent(value.id)}`, {
          name, every_minutes: every, enabled, ...targetPart,
        });
      } else {
        const [kind, id] = what.split(":");
        await api.post("/api/console/schedules", {
          name, every_minutes: every, enabled, ...targetPart,
          ...(kind === "builtin" ? { builtin: id } : { script_id: id }),
        });
      }
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <Drawer
      title={value ? "Edit schedule" : "New schedule"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save}>Save</button>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        </>
      }
    >
      {err ? <Notice>{err}</Notice> : null}

      <label className="field">
        <span>Name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Nightly inventory" />
      </label>

      {value ? (
        <div className="field">
          <span>Runs</span>
          <p className="hint" style={{ marginTop: 4 }}>
            {value.script_name} &mdash; change the script by making a new schedule.
          </p>
        </div>
      ) : (
        <label className="field">
          <span>What to run</span>
          <select className="input" value={what} onChange={(e) => setWhat(e.target.value)}>
            <option value="">Choose&hellip;</option>
            <optgroup label="Built in">
              {builtins.map((b) => (
                <option key={b.id} value={`builtin:${b.id}`}>{b.name}</option>
              ))}
            </optgroup>
            <optgroup label="Your library">
              {(scripts.data?.data ?? []).map((s) => (
                <option key={s.id} value={`script:${s.id}`}>{s.name}</option>
              ))}
            </optgroup>
          </select>
        </label>
      )}

      <label className="field">
        <span>How often</span>
        <select className="input" value={every} onChange={(e) => setEvery(Number(e.target.value))}>
          {EVERY_CHOICES.map((c) => <option key={c.minutes} value={c.minutes}>{c.label}</option>)}
        </select>
      </label>

      <label className="field">
        <span>On which machines</span>
        <select className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="all">Every machine I can reach</option>
          {(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <span className="hint">
          Checked against your access rules every time it runs, not just now &mdash; a schedule
          can never reach further than you can.
        </span>
      </label>

      <label className="check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Enabled</span>
      </label>
    </Drawer>
  );
}

// ------------------------------------------------------------- grants ----

function Grants({ me }: { me: PageProps["me"] }) {
  const list = useAsync(() => api.get<{ rows: GrantRow[] }>("/api/console/access/grants"));
  const [open, setOpen] = useState(false);
  const [revoking, setRevoking] = useState<GrantRow | null>(null);
  const canWrite = has(me, "acl:write");

  // An expiry that has passed should stop saying "active" without a reload.
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const rows = list.data?.rows ?? [];

  return (
    <>
      <div className="row row--between">
        <p className="sub" style={{ margin: 0 }}>
          Access that removes itself. Give someone what they need for as long as they need it,
          and stop remembering to take it back.
        </p>
        {canWrite ? (
          <button className="btn btn--primary btn--sm" onClick={() => setOpen(true)}>Grant access</button>
        ) : null}
      </div>

      {list.error ? <Notice>{list.error}</Notice> : null}

      {!list.loading && !rows.length ? (
        <Empty
          title="No temporary access has been given"
          hint="Grants appear here while they last, and stay as a record afterwards."
          action={canWrite
            ? <button className="btn btn--primary" onClick={() => setOpen(true)}>Grant access</button>
            : undefined}
        />
      ) : (
        <div className="panel">
          <table className="data">
            <thead>
              <tr>
                <th>Who</th><th>What</th><th>May</th><th>Reason</th><th>Ends</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const live = r.expires_at > Date.now();
                return (
                  <tr key={r.id} className={live ? "" : "row--dim"}>
                    <td>{r.user_name}<div className="sub">granted by {r.granted_by_name}</div></td>
                    <td>{r.object_name}</td>
                    <td>{r.permissions.join(", ") || "\u2014"}</td>
                    <td>{r.reason}</td>
                    <td>
                      {live
                        ? <><span className="tag tag--live">Active</span>{" "}{relativeTime(r.expires_at)}</>
                        : <span className="sub">Ended {relativeTime(r.expires_at)}</span>}
                    </td>
                    <td className="right">
                      {live && canWrite ? (
                        <button className="btn btn--ghost btn--sm" onClick={() => setRevoking(r)}>
                          End now
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open ? (
        <GrantDrawer onClose={() => setOpen(false)} onSaved={() => { setOpen(false); list.reload(); }} />
      ) : null}

      {revoking ? (
        <Confirm
          title="End this access now?"
          body={<>{revoking.user_name} will lose access to {revoking.object_name} immediately.</>}
          confirmLabel="End access"
          danger
          onCancel={() => setRevoking(null)}
          onConfirm={async () => {
            await api.del(`/api/console/access/grants/${encodeURIComponent(revoking.id)}`);
            setRevoking(null);
            list.reload();
          }}
        />
      ) : null}
    </>
  );
}

function GrantDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const users = useAsync(() => api.get<Paged<User>>("/api/console/users?pageSize=500"));
  const devices = useAsync(() => api.get<{ rows: FleetRow[] }>("/api/console/fleet"));
  const [userId, setUserId] = useState("");
  const [object, setObject] = useState("");
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState("");
  const [perms, setPerms] = useState<string[]>(["keyboard", "clipboard"]);
  const [err, setErr] = useState("");

  async function save() {
    setErr("");
    try {
      await api.post("/api/console/access/grant", {
        user_id: userId,
        object_type: object === "all" ? "all" : "device",
        object_id: object === "all" ? "" : object,
        minutes, reason, permissions: perms,
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <Drawer
      title="Grant temporary access"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save}>Grant</button>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        </>
      }
    >
      {err ? <Notice>{err}</Notice> : null}

      <label className="field">
        <span>Who</span>
        <select className="input" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Choose a person&hellip;</option>
          {(users.data?.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>{u.display_name || u.name}</option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>To reach</span>
        <select className="input" value={object} onChange={(e) => setObject(e.target.value)}>
          <option value="">Choose a machine&hellip;</option>
          <option value="all">Every machine</option>
          {(devices.data?.rows ?? []).map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </label>

      <div className="field">
        <span>And may</span>
        <div className="checks">
          {GRANT_PERMISSIONS.map((p) => (
            <label key={p.id} className="check">
              <input
                type="checkbox"
                checked={perms.includes(p.id)}
                onChange={(e) => setPerms((prev) =>
                  e.target.checked ? [...prev, p.id] : prev.filter((x) => x !== p.id))}
              />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
      </div>

      <label className="field">
        <span>For</span>
        <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
          {GRANT_MINUTES.map((m) => <option key={m.minutes} value={m.minutes}>{m.label}</option>)}
        </select>
      </label>

      <label className="field">
        <span>Why</span>
        <input
          className="input" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Ticket 4821 - printer driver"
        />
        <span className="hint">Kept with the grant, so the record says what it was for.</span>
      </label>
    </Drawer>
  );
}
